import { auth } from '../lib/firebase';

const WORKER_URL = 'https://edumi-upload-api.edumi-subdomin.workers.dev';

export async function uploadToR2(
  file: File, 
  userId: string, 
  folder: string = 'uploads'
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'User not authenticated' };
    }

    const idToken = await user.getIdToken(true);
    
    console.log('🔄 Uploading to R2 via Cloudflare Worker...');
    console.log('📁 Folder:', folder);
    console.log('📄 File:', file.name, file.type, file.size);

    // Upload directly using binary-upload endpoint
    const uploadResponse = await fetch(`${WORKER_URL}/binary-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': encodeURIComponent(file.name),
        'X-File-Size': file.size.toString(),
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('❌ Upload failed:', errorText);
      return { success: false, error: `Upload failed: ${errorText}` };
    }

    const result = await uploadResponse.json();
    console.log('✅ File uploaded to R2:', result.publicUrl);
    
    return { success: true, url: result.publicUrl };
  } catch (error) {
    console.error('❌ Error uploading to R2:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function uploadPaymentSlipToR2(file: File, userId: string): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken(true);
  
  // Generate unique filename
  const timestamp = Date.now();
  const fileExtension = file.name.split('.').pop();
  const fileName = `payment-slips/${userId}/${timestamp}.${fileExtension}`;
  
  // Get upload URL from worker
  const uploadUrlResponse = await fetch(`${WORKER_URL}/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName,
      fileType: file.type,
      fileSize: file.size,
    }),
  });

  if (!uploadUrlResponse.ok) {
    const error = await uploadUrlResponse.json();
    throw new Error(error.error || 'Failed to get upload URL');
  }

  const { uploadUrl, publicUrl } = await uploadUrlResponse.json();
  
  // Upload file to R2
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file to R2');
  }

  console.log('✅ Payment slip uploaded to R2:', publicUrl);
  return publicUrl;
}

export async function deleteFromR2(key: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idToken = await user.getIdToken(true);
  
  const response = await fetch(`${WORKER_URL}/delete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ key }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete file');
  }

  console.log('✅ File deleted from R2:', key);
}
